import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  TrendingUp, 
  BarChart3,
  Building2,
  Grid3X3,
  Image as ImageIcon
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { ApprovalStats as ApprovalStatsType } from '@/types/gallery';

// Utility to safely query a table with error handling
const safeTableQuery = async (tableName: string, query: string = '*', filters: Record<string, any> = {}) => {
  try {
    let queryBuilder = supabase.from(tableName).select(query, { count: 'exact', head: true });
    
    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      queryBuilder = queryBuilder.eq(key, value);
    });
    
    const result = await queryBuilder;
    
    if (result.error) {
      console.warn(`Error querying ${tableName}:`, result.error.message);
      return { count: 0, exists: false, error: result.error.message };
    }
    
    return { count: result.count || 0, exists: true, error: null };
  } catch (e) {
    console.warn(`Failed to query ${tableName}:`, e);
    return { count: 0, exists: false, error: String(e) };
  }
};

// Utility to safely query table data with error handling
const safeDataQuery = async (tableName: string, query: string, filters: Record<string, any> = {}) => {
  try {
    let queryBuilder = supabase.from(tableName).select(query);
    
    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      queryBuilder = queryBuilder.eq(key, value);
    });
    
    const result = await queryBuilder;
    
    if (result.error) {
      console.warn(`Error querying ${tableName} data:`, result.error.message);
      return { data: [], exists: false, error: result.error.message };
    }
    
    return { data: result.data || [], exists: true, error: null };
  } catch (e) {
    console.warn(`Failed to query ${tableName} data:`, e);
    return { data: [], exists: false, error: String(e) };
  }
};

// Fetch approval statistics
const fetchApprovalStats = async (): Promise<ApprovalStatsType> => {
  console.log('🔍 Fetching approval statistics...');

  // Check which tables exist and get counts
  const [galleriesCount, imagesCount] = await Promise.all([
    safeTableQuery('galleries', '*', { review_status: 'pending' }),
    safeTableQuery('franchise_image_jobs', '*', { review_status: 'pending' })
  ]);

  // Check approved/rejected counts
  const [galleriesApproved, galleriesRejected, imagesApproved, imagesRejected] = await Promise.all([
    safeTableQuery('galleries', '*', { review_status: 'approved' }),
    safeTableQuery('galleries', '*', { review_status: 'rejected' }),
    safeTableQuery('franchise_image_jobs', '*', { review_status: 'approved' }),
    safeTableQuery('franchise_image_jobs', '*', { review_status: 'rejected' })
  ]);

  // Get region and service breakdowns from existing tables only
  const [galleriesData, imagesData] = await Promise.all([
    galleriesCount.exists ? safeDataQuery('galleries', 'franchise_region, primary_service', { review_status: 'pending' }) : { data: [], exists: false },
    imagesCount.exists ? safeDataQuery('franchise_image_jobs', 'franchise_region', { review_status: 'pending' }) : { data: [], exists: false }
  ]);

  // Calculate region breakdown
  const by_region: Record<string, number> = {};
  [...(galleriesData.data || []), ...(imagesData.data || [])].forEach((item: any) => {
    const region = item.franchise_region || 'general';
    by_region[region] = (by_region[region] || 0) + 1;
  });

  // Calculate service breakdown
  const by_service: Record<string, number> = {};
  (galleriesData.data || []).forEach((gallery: any) => {
    if (gallery.primary_service) {
      by_service[gallery.primary_service] = (by_service[gallery.primary_service] || 0) + 1;
    }
  });

  const pending_galleries = galleriesCount.count || 0;
  const pending_images = imagesCount.count || 0;
  
  const stats = {
    total_pending: pending_galleries + pending_images,
    pending_galleries,
    pending_images,
    approved_today: (galleriesApproved.count || 0) + (imagesApproved.count || 0),
    rejected_today: (galleriesRejected.count || 0) + (imagesRejected.count || 0),
    by_region,
    by_service
  };

  console.log('📊 Approval stats:', {
    ...stats,
    tableStatus: {
      galleries: galleriesCount.exists ? 'exists' : `missing (${galleriesCount.error})`,
      franchise_image_jobs: imagesCount.exists ? 'exists' : `missing (${imagesCount.error})`
    }
  });
  
  return stats;
};

const ApprovalStats: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['approval-stats'],
    queryFn: fetchApprovalStats,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000 // Consider data stale after 30 seconds
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !stats) {
    return (
      <Card className="col-span-full">
        <CardContent className="p-6 text-center text-red-600">
          Failed to load approval statistics
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Pending</p>
                <p className="text-2xl font-bold">{stats.total_pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Grid3X3 className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Galleries</p>
                <p className="text-2xl font-bold">{stats.pending_galleries}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <ImageIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Images</p>
                <p className="text-2xl font-bold">{stats.pending_images}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Approved</p>
                <p className="text-2xl font-bold">{stats.approved_today}</p>
                <p className="text-xs text-gray-500">
                  {stats.rejected_today} rejected
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Region */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              By Region
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(stats.by_region).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.by_region)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([region, count]) => (
                    <div key={region} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">
                        {region.replace('_', ' ')}
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No regional data available
              </p>
            )}
          </CardContent>
        </Card>

        {/* By Service */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              By Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(stats.by_service).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.by_service)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([service, count]) => (
                    <div key={service} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">
                        {service.replace('_', ' ')}
                      </span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No service data available
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApprovalStats;